import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, Trash2, Copy, ShieldCheck, Eye } from "lucide-react";

const genPassword = () =>
  "Vk" + Math.random().toString(36).slice(2, 8) + "!" + Math.random().toString(36).slice(2, 6).toUpperCase();

export default function TeamPanel() {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", level: "staff", temp_password: genPassword() });
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    const { data } = await api.get("/team");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team", form);
      setLastCreated({ email: form.email, temp_password: form.temp_password, level: form.level });
      toast.success(`${form.level === "admin" ? "Admin" : "Staff"} account created`);
      setForm({ email: "", name: "", level: "staff", temp_password: genPassword() });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the team? They lose access immediately.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Team member removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(
      `Vismarks ${lastCreated.level} access\nURL: ${window.location.origin}/login\nEmail: ${lastCreated.email}\nTemp password: ${lastCreated.temp_password}\n(You'll be asked to set your own password on first login)`
    );
    toast.success("Credentials copied — share them securely");
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="team-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#007AFF]" />
        <h2 className="font-black text-xl tracking-tight">Team access</h2>
      </div>
      <p className="mt-1 text-xs text-neutral-500">
        Create individual accounts for teammates. <span className="text-neutral-300">Admin</span> = full control ·{" "}
        <span className="text-neutral-300">Staff</span> = view dashboards &amp; waitlist only. Only you can manage this list.
      </p>

      <form onSubmit={create} className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Input required type="email" placeholder="teammate@email.com" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="team-email-input" className="bg-[#0A0A0A] border-[#27272A] lg:col-span-2" />
        <Input required minLength={2} placeholder="Full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="team-name-input" className="bg-[#0A0A0A] border-[#27272A]" />
        <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
          data-testid="team-level-select"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#007AFF]">
          <option value="staff">Staff (view only)</option>
          <option value="admin">Admin (full)</option>
        </select>
        <Button type="submit" disabled={busy} data-testid="team-create-btn"
          className="bg-white text-black hover:bg-neutral-200 rounded-full h-10 font-semibold">
          <UserPlus className="h-4 w-4 mr-1" /> {busy ? "Creating…" : "Add"}
        </Button>
      </form>
      <p className="mt-2 text-[11px] text-neutral-600 font-mono">
        Temp password: {form.temp_password} <span className="text-neutral-500 font-sans">(auto-generated — they'll change it on first login)</span>
      </p>

      {lastCreated && (
        <div data-testid="team-last-created" className="mt-4 rounded-md border border-[#34C759]/40 bg-[#34C759]/[0.08] px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span>
            <span className="text-white font-semibold">{lastCreated.email}</span>{" "}
            <span className="text-neutral-400">temp password:</span>{" "}
            <span className="font-mono text-white">{lastCreated.temp_password}</span>
          </span>
          <Button size="sm" variant="outline" onClick={copyCreds} data-testid="team-copy-creds-btn"
            className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/5">
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy credentials
          </Button>
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="team-member-list">
        {members === null ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : members.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-md border border-[#27272A] bg-[#0A0A0A] px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border flex-shrink-0 ${m.role === "admin" ? "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10" : "text-neutral-400 border-white/20 bg-white/5"}`}>
                {m.is_root ? "root" : m.role}
              </span>
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{m.name || m.email}</div>
                <div className="text-xs text-neutral-500 truncate">{m.email}{m.must_change_password ? " · hasn't logged in yet" : ""}</div>
              </div>
            </div>
            {!m.is_root && (
              <Button size="sm" variant="ghost" onClick={() => remove(m)} data-testid={`team-remove-${m.email}`}
                className="text-neutral-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 flex-shrink-0">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
